<template>
  <div class="eb-editor-wrapper" :class="{ 'eb-email-mode': mode === 'email' }">
    <div class="eb-main-area">
      <div class="eb-sidebar">
        <div class="eb-sidebar-header"></div>
        <div class="eb-sidebar-body"></div>
        <div class="eb-sidebar-footer"></div>
      </div>
      <div class="eb-canvas-area">
        <div class="eb-canvas-toolbar"></div>
        <div ref="editorContainer" class="eb-gjs-container"></div>
      </div>
    </div>
  </div>
</template>

<script>
import 'grapesjs/dist/css/grapes.min.css'
import grapesjs from 'grapesjs/dist/grapes.min.js'
import grapesjsPresetNewsletter from 'grapesjs-preset-newsletter'
import axios from 'axios'

function v2Admin(path) {
  const v1Base = axios.defaults.baseURL || ''
  return v1Base.replace(/\/api\/v1\/?$/, '/api/v2/admin/') + path
}

import elementorLayout from './plugins/elementor-layout'
import elementorWidgets from './plugins/elementor-widgets'
import elementorPanels from './plugins/elementor-panels'
import elementorResponsive from './plugins/elementor-responsive'
import elementorGlobalStyles from './plugins/elementor-global-styles'
import elementorTemplates from './plugins/elementor-templates'
import elementorNavigator from './plugins/elementor-navigator'
import elementorCustomCss from './plugins/elementor-custom-css'
import elementorEmail from './plugins/elementor-email'
import styleSectors from './config/style-sectors'
import { VISIBILITY_STYLES } from './config/breakpoints'

import './PageBuilder.scss'

export default {
  name: 'PageBuilder',
  props: {
    value: {
      type: String,
      default: '',
    },
    mode: {
      type: String,
      default: 'page',
      validator: v => ['page', 'email'].includes(v),
    },
    templateVars: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      editor: null,
    }
  },
  mounted() {
    this.initEditor()
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy()
      this.editor = null
    }
  },
  methods: {
    initEditor() {
      const isEmail = this.mode === 'email'

      const config = {
        container: this.$refs.editorContainer,
        height: '100%',
        width: '100%',
        fromElement: false,
        storageManager: false,
        allowScripts: 1,
        notTextTags: ['button', 'a', 'input', 'select', 'textarea'],
        canvas: {
          styles: [
            'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Roboto:wght@300;400;500;700&family=Open+Sans:wght@300;400;600;700&family=Lato:wght@300;400;700&family=Montserrat:wght@300;400;500;600;700&family=Poppins:wght@300;400;500;600;700&family=Playfair+Display:wght@400;500;600;700&display=swap',
          ],
        },
        richTextEditor: {
          actions: ['bold', 'italic', 'underline', 'strikethrough', 'link'],
        },
        assetManager: {
          uploadFile: (e) => this.handleAssetUpload(e),
          autoAdd: true,
        },
      }

      if (isEmail) {
        Object.assign(config, {
          plugins: [grapesjsPresetNewsletter, elementorPanels, elementorNavigator, elementorTemplates, elementorGlobalStyles, elementorEmail],
          pluginsOpts: {
            [grapesjsPresetNewsletter]: {
              inlineCss: true,
              updateStyleManager: false,
              cellStyle: {
                'font-size': '14px',
                'font-weight': 300,
                'vertical-align': 'top',
                color: 'rgb(111, 119, 125)',
                margin: 0,
                padding: 0,
              },
            },
          },
          avoidInlineStyle: false,
          forceClass: false,
          keepUnusedStyles: true,
          styleManager: {
            clearProperties: false,
            sectors: styleSectors,
          },
        })
      } else {
        Object.assign(config, {
          plugins: [elementorLayout, elementorWidgets, elementorPanels, elementorResponsive, elementorGlobalStyles, elementorTemplates, elementorNavigator, elementorCustomCss],
          avoidInlineStyle: true,
          forceClass: true,
          cssComposer: { keepUnusedStyles: false },
          styleManager: {
            clearProperties: false,
            sectors: styleSectors,
          },
        })
      }

      this.editor = grapesjs.init(config)
      this.editor.emailMode = isEmail

      this.editor.on('load', () => {
        this.registerTemplateVarBlocks()
        this.registerEventWidgets()
        this.injectCanvasBaseStyles()
        if (this.value) {
          this.setHtml(this.value)
        }
        this.$emit('editor-ready', this.editor)
      })

      this.editor.on('update', () => {
        this.$emit('input', this.getHtml())
      })
    },

    getHtml() {
      if (!this.editor) return ''

      const html = this.editor.getHtml()
      const css = this.editor.getCss()

      if (this.mode === 'email') {
        return this.buildInlineHtml(html, css)
      }

      let fullCss = css || ''
      if (this.editor.getGlobalStylesCss) {
        fullCss = this.editor.getGlobalStylesCss() + '\n' + fullCss
      }
      if (this.editor.getCustomCss) {
        const customCss = this.editor.getCustomCss()
        if (customCss) fullCss += '\n' + customCss
      }
      if (html.includes('eb-hide-')) {
        fullCss += '\n' + VISIBILITY_STYLES
      }

      let cleanHtml = html
        .replace(/\s*data-gjs-type="[^"]*"/g, '')
        .replace(/\s*data-gjs[^=]*="[^"]*"/g, '')
        .replace(/\s*style=""/g, '')

      if (!fullCss) return cleanHtml
      return `<style>${fullCss}</style>\n${cleanHtml}`
    },

    getCss() {
      if (!this.editor) return ''
      return this.editor.getCss() || ''
    },

    setHtml(html) {
      if (!this.editor) return

      let htmlContent = html
      let cssContent = ''

      const styleMatch = html.match(/<style[^>]*>([\s\S]*?)<\/style>/i)
      if (styleMatch) {
        cssContent = styleMatch[1]
        htmlContent = html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '').trim()
      }

      this.editor.setComponents(htmlContent)
      if (cssContent) {
        this.editor.setStyle(cssContent)
      }
    },

    clearCanvas() {
      if (!this.editor) return
      this.editor.DomComponents.clear()
      this.editor.CssComposer.clear()
    },

    async handleAssetUpload(e) {
      const files = e.dataTransfer ? e.dataTransfer.files : e.target.files
      if (!files || !files.length) return

      const formData = new FormData()
      for (let i = 0; i < files.length; i++) {
        formData.append('files[]', files[i])
      }

      try {
        const response = await axios.post(v2Admin('designer/upload-image'), formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        const data = response.data?.success?.data?.data || response.data?.data?.data || []
        const am = this.editor.AssetManager
        data.forEach(asset => {
          am.add(asset.src)
        })
      } catch (err) {
        console.error('Image upload failed:', err)
      }
    },

    buildInlineHtml(html, css) {
      if (!css) return html
      try {
        const parser = new DOMParser()
        const doc = parser.parseFromString(
          `<html><head><style>${css}</style></head><body>${html}</body></html>`,
          'text/html'
        )

        const styleEl = doc.querySelector('style')
        if (styleEl) {
          const rules = this.parseCssRules(styleEl.textContent)
          rules.forEach(rule => {
            try {
              const elements = doc.querySelectorAll(rule.selector)
              elements.forEach(el => {
                Object.keys(rule.styles).forEach(prop => {
                  if (!el.style.getPropertyValue(prop)) {
                    el.style.setProperty(prop, rule.styles[prop])
                  }
                })
              })
            } catch (e) { /* skip invalid selectors */ }
          })
          styleEl.remove()
        }

        return doc.body.innerHTML
      } catch (e) {
        return html
      }
    },

    parseCssRules(cssText) {
      const rules = []
      const regex = /([^{]+)\{([^}]*)\}/g
      let match
      while ((match = regex.exec(cssText)) !== null) {
        const selector = match[1].trim()
        const styles = {}
        match[2].split(';').forEach(decl => {
          const parts = decl.split(':')
          if (parts.length === 2) {
            styles[parts[0].trim()] = parts[1].trim()
          }
        })
        if (selector && !selector.startsWith('@')) {
          rules.push({ selector, styles })
        }
      }
      return rules
    },

    registerTemplateVarBlocks() {
      if (!this.editor || !this.templateVars || !this.templateVars.length) return

      const bm = this.editor.BlockManager
      const dc = this.editor.DomComponents

      dc.addType('eb-template-var', {
        isComponent(el) {
          return el.getAttribute && el.getAttribute('data-gjs-type') === 'eb-template-var'
        },
        model: {
          defaults: {
            name: 'Variable',
            tagName: 'span',
            draggable: true,
            droppable: false,
            editable: false,
            attributes: { 'data-gjs-type': 'eb-template-var' },
            style: {
              'display': 'inline-block',
              'background': 'linear-gradient(135deg, #f6d365, #fda085)',
              'color': '#333',
              'padding': '2px 8px',
              'border-radius': '4px',
              'font-size': '13px',
              'font-weight': '600',
              'font-family': 'monospace',
            },
          },
          toHTML() {
            return this.get('varOutput') || ''
          },
        },
        view: {
          onRender() {
            const varName = this.model.get('varName') || ''
            this.el.textContent = '{{ ' + varName + ' }}'
          },
        },
      })

      this.templateVars.forEach(v => {
        const varName = typeof v === 'string' ? v : v.name || v.key
        const varLabel = typeof v === 'string'
          ? v.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
          : v.label || varName

        bm.add('eb-var-' + varName, {
          label: varLabel,
          category: 'Variables',
          media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h2v2h-2v-2zm0-8h2v6h-2V9zm1-4C8.69 5 5.69 8 5.69 12S8.69 19 12 19s6.31-3 6.31-7S15.31 5 12 5z"/></svg>',
          content: {
            type: 'eb-template-var',
            varName: varName,
            varOutput: '{{' + varName + '}}',
            content: '{{ ' + varName + ' }}',
          },
        })
      })
    },

    registerEventWidgets() {
      if (!this.editor) return

      const bm = this.editor.BlockManager
      const dc = this.editor.DomComponents

      dc.addType('eb-rsvp-action', {
        isComponent(el) {
          return el.getAttribute && el.getAttribute('data-rsvp-action') !== null
        },
        model: {
          defaults: {
            name: 'RSVP Action',
            tagName: 'div',
            droppable: false,
            attributes: { 'data-rsvp-action': 'true', 'data-gjs-type': 'eb-rsvp-action' },
            style: {
              'text-align': 'center',
              'padding': '40px 20px',
              'background': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              'border-radius': '12px',
            },
          },
          toHTML() {
            return '<div data-rsvp-action="true" style="text-align:center;padding:40px 20px;">{{rsvp_action}}</div>'
          },
        },
        view: {
          onRender() {
            this.el.innerHTML = '<div style="color:#fff;font-size:18px;font-weight:700;margin-bottom:8px;">RSVP Action Widget</div><div style="color:rgba(255,255,255,0.8);font-size:13px;">This area will display the RSVP form at runtime</div>'
          },
        },
      })

      bm.add('eb-rsvp-action', {
        label: 'RSVP Action',
        category: 'Event',
        media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M9 11.75A1.25 1.25 0 0 0 7.75 13 1.25 1.25 0 0 0 9 14.25 1.25 1.25 0 0 0 10.25 13 1.25 1.25 0 0 0 9 11.75M15 11.75A1.25 1.25 0 0 0 13.75 13 1.25 1.25 0 0 0 15 14.25 1.25 1.25 0 0 0 16.25 13 1.25 1.25 0 0 0 15 11.75M12 2A10 10 0 0 0 2 12A10 10 0 0 0 12 22A10 10 0 0 0 22 12A10 10 0 0 0 12 2M12 20A8 8 0 0 1 4 12A8 8 0 0 1 12 4A8 8 0 0 1 20 12A8 8 0 0 1 12 20M12 17.23C10.25 17.23 8.71 16.5 7.81 15.42L9.23 14C9.68 14.72 10.75 15.23 12 15.23S14.32 14.72 14.77 14L16.19 15.42C15.29 16.5 13.75 17.23 12 17.23Z"/></svg>',
        content: { type: 'eb-rsvp-action' },
      })

      dc.addType('eb-registration-form', {
        isComponent(el) {
          return el.getAttribute && el.getAttribute('data-registration-form') !== null
        },
        model: {
          defaults: {
            name: 'Registration Form',
            tagName: 'div',
            droppable: false,
            attributes: { 'data-registration-form': 'true', 'data-gjs-type': 'eb-registration-form' },
            style: {
              'text-align': 'center',
              'padding': '40px 20px',
              'background': '#f0f4ff',
              'border': '2px dashed #667eea',
              'border-radius': '12px',
            },
          },
          toHTML() {
            return '<div data-registration-form="true">{{registration_form}}</div>'
          },
        },
        view: {
          onRender() {
            this.el.innerHTML = '<div style="color:#667eea;font-size:18px;font-weight:700;margin-bottom:8px;"><i class="fa fa-wpforms" style="margin-right:8px;"></i>Registration Form</div><div style="color:#666;font-size:13px;">Registration form fields will appear here at runtime</div>'
          },
        },
      })

      bm.add('eb-registration-form', {
        label: 'Registration Form',
        category: 'Event',
        media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M17,7H22V17H17V19A1,1 0 0,0 18,20H20V22H17.5C16.95,22 16,21.55 16,21C16,21.55 15.05,22 14.5,22H12V20H14A1,1 0 0,0 15,19V5A1,1 0 0,0 14,4H12V2H14.5C15.05,2 16,2.45 16,3C16,2.45 16.95,2 17.5,2H20V4H18A1,1 0 0,0 17,5V7M2,7H13V9H4V15H13V17H2V7M20,15V9H17V15H20M8.5,12A1.5,1.5 0 0,0 7,10.5A1.5,1.5 0 0,0 5.5,12A1.5,1.5 0 0,0 7,13.5A1.5,1.5 0 0,0 8.5,12M11,12A1.5,1.5 0 0,0 9.5,10.5A1.5,1.5 0 0,0 8,12A1.5,1.5 0 0,0 9.5,13.5A1.5,1.5 0 0,0 11,12Z"/></svg>',
        content: { type: 'eb-registration-form' },
      })

      dc.addType('eb-event-countdown', {
        isComponent(el) {
          return el.getAttribute && el.getAttribute('data-event-countdown') !== null
        },
        model: {
          defaults: {
            name: 'Event Countdown',
            tagName: 'div',
            droppable: false,
            attributes: { 'data-event-countdown': 'true', 'data-gjs-type': 'eb-event-countdown' },
            style: {
              'text-align': 'center',
              'padding': '40px 20px',
              'background': 'linear-gradient(135deg, #0c0c1d 0%, #1a1a3e 100%)',
              'border-radius': '12px',
            },
          },
          toHTML() {
            return '<div data-event-countdown="true" style="text-align:center;padding:40px 20px;background:linear-gradient(135deg,#0c0c1d,#1a1a3e);border-radius:12px;"><div style="color:#fff;font-size:14px;text-transform:uppercase;letter-spacing:2px;margin-bottom:20px;">Event Starts In</div><div id="eb-event-cd" style="display:flex;justify-content:center;gap:20px;"><div style="text-align:center;"><div style="font-size:36px;font-weight:700;color:#667eea;">--</div><div style="font-size:11px;color:rgba(255,255,255,0.6);text-transform:uppercase;">Days</div></div><div style="text-align:center;"><div style="font-size:36px;font-weight:700;color:#667eea;">--</div><div style="font-size:11px;color:rgba(255,255,255,0.6);text-transform:uppercase;">Hours</div></div><div style="text-align:center;"><div style="font-size:36px;font-weight:700;color:#667eea;">--</div><div style="font-size:11px;color:rgba(255,255,255,0.6);text-transform:uppercase;">Minutes</div></div><div style="text-align:center;"><div style="font-size:36px;font-weight:700;color:#667eea;">--</div><div style="font-size:11px;color:rgba(255,255,255,0.6);text-transform:uppercase;">Seconds</div></div></div><div style="color:rgba(255,255,255,0.5);font-size:12px;margin-top:16px;">Countdown uses {{event_date}}</div></div>'
          },
        },
        view: {
          onRender() {
            this.el.innerHTML = '<div style="color:#fff;font-size:14px;text-transform:uppercase;letter-spacing:2px;margin-bottom:20px;">Event Starts In</div><div style="display:flex;justify-content:center;gap:20px;"><div style="text-align:center;"><div style="font-size:36px;font-weight:700;color:#667eea;">12</div><div style="font-size:11px;color:rgba(255,255,255,0.6);text-transform:uppercase;">Days</div></div><div style="text-align:center;"><div style="font-size:36px;font-weight:700;color:#667eea;">08</div><div style="font-size:11px;color:rgba(255,255,255,0.6);text-transform:uppercase;">Hours</div></div><div style="text-align:center;"><div style="font-size:36px;font-weight:700;color:#667eea;">45</div><div style="font-size:11px;color:rgba(255,255,255,0.6);text-transform:uppercase;">Minutes</div></div><div style="text-align:center;"><div style="font-size:36px;font-weight:700;color:#667eea;">30</div><div style="font-size:11px;color:rgba(255,255,255,0.6);text-transform:uppercase;">Seconds</div></div></div><div style="color:rgba(255,255,255,0.5);font-size:12px;margin-top:16px;">Uses {{event_date}} at runtime</div>'
          },
        },
      })

      bm.add('eb-event-countdown', {
        label: 'Event Countdown',
        category: 'Event',
        media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M12 20A8 8 0 0 1 4 12A8 8 0 0 1 12 4A8 8 0 0 1 20 12A8 8 0 0 1 12 20M12 2A10 10 0 0 0 2 12A10 10 0 0 0 12 22A10 10 0 0 0 22 12A10 10 0 0 0 12 2M12.5 7V12.25L17 14.92L16.25 16.15L11 13V7H12.5Z"/></svg>',
        content: { type: 'eb-event-countdown' },
      })
    },

    injectCanvasBaseStyles() {
      if (!this.editor) return

      const frame = this.editor.Canvas.getFrameEl()
      if (!frame) return
      const doc = frame.contentDocument
      if (!doc) return

      let styleEl = doc.getElementById('eb-canvas-base')
      if (!styleEl) {
        styleEl = doc.createElement('style')
        styleEl.id = 'eb-canvas-base'
        doc.head.appendChild(styleEl)
      }

      styleEl.textContent = `
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Open Sans', sans-serif;
          color: #333;
          line-height: 1.6;
          margin: 0;
          padding: 0;
        }
        *, *::before, *::after {
          box-sizing: border-box;
        }
        img {
          max-width: 100%;
          height: auto;
        }
        [data-gjs-type="eb-template-var"] {
          display: inline-block;
          background: linear-gradient(135deg, #f6d365, #fda085);
          color: #333;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 600;
          font-family: monospace;
        }
        [data-rsvp-action],
        [data-registration-form],
        [data-event-countdown] {
          min-height: 60px;
        }
      `
    },
  },
}
</script>

<style scoped>
.eb-gjs-container {
  width: 100%;
  height: 100%;
}
</style>
